/**
 * SEO data configuration — page-level metadata + JSON-LD structured data.
 *
 * Used by `<SEO />` (src/components/SEO.js). Each entry can include:
 *   - title, description, keywords, image, url, type, locale
 *   - structuredData: a single JSON-LD object OR an array of objects
 *
 * The JSON-LD payloads are linked together via @id where useful so that
 * crawlers can stitch the knowledge graph (Organization ↔ WebSite ↔ Page).
 */

const baseUrl = 'https://www.codenticai.com';
const orgId = `${baseUrl}/#organization`;
const siteId = `${baseUrl}/#website`;
// Square 512x512 tile generated by scripts/generate-icons.py.
// (logo.jpeg is a 1219x295 banner, so it must not be declared as a square logo.)
const logoUrl = `${baseUrl}/logo512.png`;
const ogImage = `${baseUrl}/og-image.png`;

/* ------------------------------------------------------------------ */
/*  Reusable JSON-LD building blocks                                  */
/* ------------------------------------------------------------------ */

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': orgId,
  name: 'Codentic AI',
  alternateName: 'Codenticai',
  url: baseUrl,
  logo: {
    '@type': 'ImageObject',
    url: logoUrl,
    width: 512,
    height: 512
  },
  image: ogImage,
  description:
    'Applied AI research and product company working on Emerging World Models (EWM) — a structural memory and working-context side-car beside any AI model, never a replacement for it. The host keeps its weights; the memory can be measured, verified and carried across models.',
  foundingDate: '2024',
  knowsAbout: [
    'Artificial Intelligence',
    'AI Memory Layer',
    'Structural Memory',
    'World Models',
    'Emerging World Models',
    'HLLSet Algebra',
    'Content-Addressed Storage',
    'On-Premise AI',
    'Applied AI Research',
    'Robotics',
    'ROS 2',
    'Natural Language Processing',
    'MLOps',
    'Knowledge Graphs',
    'Retrieval-Augmented Generation',
    'Metadata Management',
    'Project Management Software',
    'Government Digital Transformation'
  ],
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'IN',
    addressLocality: 'India'
  },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+91-9845555760',
      contactType: 'customer support',
      email: 'support@codenticai.com',
      availableLanguage: ['English', 'Arabic', 'Hindi']
    },
    {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: 'support@codenticai.com',
      availableLanguage: ['English', 'Arabic']
    }
  ],
  sameAs: []
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': siteId,
  url: baseUrl,
  name: 'Codentic AI',
  publisher: { '@id': orgId },
  inLanguage: 'en'
};

const breadcrumb = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${baseUrl}${it.url}`
  }))
});

const webPage = ({ name, description, url }) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${baseUrl}${url}#webpage`,
  url: `${baseUrl}${url}`,
  name,
  description,
  isPartOf: { '@id': siteId },
  about: { '@id': orgId }
});

/* ------------------------------------------------------------------ */
/*  Per-page SEO configuration                                        */
/* ------------------------------------------------------------------ */

const seoData = {
  home: {
    title: 'Codentic AI — Emerging World Models: Memory Beside Any AI Model',
    description:
      'Emerging World Models (EWM) is a structural memory and working-context side-car that attaches beside any AI model you already run. The host keeps its weights, tokenizer and APIs — no retraining, no fine-tuning. Measured, content-addressed world-model memory that runs on-premise, inside your own boundary. Plus NanoLM, the AI Enterprise Suite and an active research portfolio.',
    keywords:
      'Emerging World Models, EWM, world-model memory, world model side-car, AI memory layer, structural memory, AI side-car, JEPA, latent predictive architectures, HLLSet algebra, content-addressed memory, on-premise AI, air-gapped AI, long context alternative, agent memory, auditable AI, deterministic AI, NanoLM, AI-PMS, SGS.ai, inventory management system, laboratory information system, NLP, MLOps, ROS 2 robotics, government AI',
    url: baseUrl,
    structuredData: [
      organizationSchema,
      websiteSchema,
      webPage({
        name: 'Codentic AI — Emerging World Models: A Memory Side-Car for Any AI Model',
        description:
          'Emerging World Models (EWM) adds a structural memory and working-context layer beside any AI model — never as a replacement for it. Measured, content-addressed memory for on-premise and air-gapped deployments.',
        url: '/'
      })
    ]
  },

  products: {
    title: 'Products — NanoLM, AI Enterprise Suite & Vertical Packs | Codentic AI',
    description:
      'NanoLM (self-hosted NLP, OCR and clinical analysis), the AI Enterprise Suite (AI-PMS, SGS.ai, Inventory Management System, LIS), and vertical packs built on the EWM side-car: clinical report triage, invoice and document extraction, and agent memory.',
    keywords:
      'NanoLM, AI-PMS, SGS.ai, inventory management system, laboratory information system, clinical report triage, invoice extraction, document extraction, agent memory, on-premise AI products, CPU-only AI',
    url: `${baseUrl}/products`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'Products', url: '/products' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Codentic AI — Products',
        itemListElement: [
          productItem(1, 'NanoLM', 'Self-hosted CPU-first AI assistant for NLP Q&A, OCR and clinical analysis'),
          productItem(2, 'AI-PMS', 'AI-Powered Project Management Platform'),
          productItem(3, 'SGS.ai', 'AI-Powered Metadata Management, Discovery & Intelligence Platform'),
          productItem(4, 'Inventory Management System', 'Inventory management with AI-driven demand forecasting'),
          productItem(5, 'LIS — Laboratory Information System', 'Laboratory workflow, sample tracking and compliance')
        ]
      }
    ]
  },

  contact: {
    title: 'Contact Us | Codentic AI',
    description:
      'Talk to Codentic AI about pilots, research partnerships, or custom AI / software engagements.',
    keywords:
      'contact Codentic AI, request demo, AI pilot, research partnership, business consultation',
    url: `${baseUrl}/contact`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'Contact', url: '/contact' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        '@id': `${baseUrl}/contact#webpage`,
        url: `${baseUrl}/contact`,
        name: 'Contact Codentic AI',
        about: { '@id': orgId },
        isPartOf: { '@id': siteId }
      }
    ]
  },

  about: {
    title: 'About Us — Applied AI Research & Product Company | Codentic AI',
    description:
      'Meet the Codentic AI founders and leadership — researchers, engineers and visionaries with decades of experience and patents across AI, robotics and enterprise software.',
    keywords:
      'about Codentic AI, AI company, AI founders, applied AI, enterprise AI, robotics, AI research company',
    url: `${baseUrl}/about`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'About', url: '/about' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        '@id': `${baseUrl}/about#webpage`,
        url: `${baseUrl}/about`,
        name: 'About Codentic AI',
        about: { '@id': orgId },
        isPartOf: { '@id': siteId }
      }
    ]
  },

  nanolm: {
    title: 'NanoLM — AI Assistant for NLP, OCR & Clinical Analysis | Codentic AI',
    description:
      'NanoLM is the Codentic AI retrieval-based assistant for NLP Q&A, document OCR and clinical analysis, powered by the Guru — Emerging World Models knowledge corpus. Self-hosted, CPU-first: no external LLM, no GPU required.',
    keywords:
      'NanoLM, AI assistant, NLP Q&A, OCR, document analysis, clinical analysis, retrieval language model, Guru EWM, self-hosted AI assistant, CPU-only AI',
    url: `${baseUrl}/nanolm`,
    // Dedicated social card — this page is promoted and shared externally.
    image: `${baseUrl}/og-nanolm.png`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'NanoLM', url: '/nanolm' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        '@id': `${baseUrl}/nanolm#webapp`,
        url: `${baseUrl}/nanolm`,
        name: 'NanoLM — AI Assistant',
        description:
          'Retrieval-based AI assistant for NLP Q&A, OCR and clinical analysis over a 55k+ card knowledge corpus.',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        provider: { '@id': orgId }
      }
    ]
  },

  memory: {
    title: 'Visual Memory — JEPA World-Model Memory for Clips | Codentic AI',
    description:
      'Visual Memory is the Codentic AI JEPA dashboard: store image and video clips as HLLSet tokens, find similar clips by Jaccard similarity, see what is genuinely new with D/R/N novelty, and compose memories with set algebra.',
    keywords:
      'visual memory, JEPA world model, HLLSet, VQ-VAE, clip retrieval, similarity search, novelty detection, DRN decomposition, set algebra, content-addressed memory, Guru EWM',
    url: `${baseUrl}/memory`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'NanoLM', url: '/nanolm' },
        { name: 'Visual Memory', url: '/memory' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        '@id': `${baseUrl}/memory#webapp`,
        url: `${baseUrl}/memory`,
        name: 'Visual Memory — JEPA Dashboard',
        description:
          'Visual memory dashboard over the JEPA world-model encoder: ingest clips into an HLLSet lattice, query similar clips, detect novelty and compose memories.',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        provider: { '@id': orgId }
      }
    ]
  },

  technology: {
    title: 'How It Works — A Memory Side-Car Beside Any AI Model | Codentic AI',
    description:
      'The EWM side-car loop: encodings in, encode, ingest to HLLSets, content-addressed state with D/R/N, ordered materialize, encodings back to the host. Five structural guarantees — idempotent, immutable, content-addressed, composable, monotonic.',
    keywords:
      'AI side-car, EWM architecture, HLLSet, IICA morphisms, content-addressed state, DRN decomposition, De Bruijn materialization, Noether union invariant, structural memory, AI memory architecture, model-agnostic memory',
    url: `${baseUrl}/technology`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'Technology', url: '/technology' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'TechArticle',
        '@id': `${baseUrl}/technology#article`,
        url: `${baseUrl}/technology`,
        headline: 'A structural memory side-car beside any AI model',
        about: { '@id': orgId },
        isPartOf: { '@id': siteId },
        author: { '@id': orgId },
        publisher: { '@id': orgId }
      }
    ]
  },

  evidence: {
    title: 'Evidence & Benchmarks — Six Measured Scenarios | Codentic AI',
    description:
      'Measured, not projected: loop accuracy 1.0 on a real vision-language-action stack, 100% word retention on 128K-vocabulary OCR, r = 0.935 agreement on vision grids, 12 crates and 240 automated tests, all on CPU-only hardware.',
    keywords:
      'AI benchmark, loop accuracy, HLLSet benchmark, OCR reconstruction, De Bruijn order, BSS similarity, vision patch grid, side-car proof of concept, measured AI results, CPU-only AI benchmark',
    url: `${baseUrl}/evidence`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'Evidence', url: '/evidence' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${baseUrl}/evidence#webpage`,
        url: `${baseUrl}/evidence`,
        name: 'Evidence & Benchmarks — Codentic AI',
        about: { '@id': orgId },
        isPartOf: { '@id': siteId }
      }
    ]
  },

  publications: {
    title: 'Publications & Published Record | Codentic AI',
    description:
      'The published record behind Emerging World Models: IJESIRD Vol. 13 Issue 2, ASTESJ 2026 and SGS 2024, plus the framework document on the four principles, HLLSet Algebra and the Grothendieck-topology target.',
    keywords:
      'Emerging World Models publication, IJESIRD, ASTESJ, HLLSet research, structural framework paper, world models research, published AI research, Kumar Mylnikov',
    url: `${baseUrl}/publications`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'Publications', url: '/publications' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${baseUrl}/publications#webpage`,
        url: `${baseUrl}/publications`,
        name: 'Publications — Codentic AI',
        about: { '@id': orgId },
        isPartOf: { '@id': siteId }
      }
    ]
  },

  scenarios: {
    title: 'Scenarios — Where the EWM Side-Car Was Run and Measured | Codentic AI',
    description:
      'Nine scenarios drawn from sixteen notebooks: video and scene understanding, autonomous driving, image generation, many-stream state, operational boot and recovery, world-model cooperation, several models in one state, and three modalities in one memory. Each page states its own limits.',
    keywords:
      'EWM scenarios, AI memory use cases, world model side-car, scene change detection, autonomous driving memory, perceptual state, denoising trajectory, operational graph, multi-model memory, multimodal memory, HLLSet, structural memory',
    url: `${baseUrl}/scenarios`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'Scenarios', url: '/scenarios' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${baseUrl}/scenarios#webpage`,
        url: `${baseUrl}/scenarios`,
        name: 'Scenarios — Codentic AI',
        about: { '@id': orgId },
        isPartOf: { '@id': siteId }
      }
    ]
  },

  applications: {
    title: 'Applications — EWM Memory by Industry | Codentic AI',
    description:
      'Where the memory side-car applies, industry by industry: IT and enterprise software, defence and national security, healthcare, insurance, banking and financial services, legal, and education and research. Each page states the constraint, what the layer provides, and the measured scenario behind it.',
    keywords:
      'on-premise AI, air-gapped AI, AI memory by industry, healthcare AI memory, clinical document AI, legal document AI, insurance claims AI, banking AI governance, defence AI, research reproducibility, data residency AI',
    url: `${baseUrl}/applications`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'Applications', url: '/applications' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${baseUrl}/applications#webpage`,
        url: `${baseUrl}/applications`,
        name: 'Applications — Codentic AI',
        about: { '@id': orgId },
        isPartOf: { '@id': siteId }
      }
    ]
  },

  trust: {
    title: 'Trust, Security & Compliance — Auditable by Construction | Codentic AI',
    description:
      'The model is never modified and your data never leaves your boundary. Immutable, content-addressed commits give an audit trail by construction — D/R/N shows what was dropped, retained and new. No GPU required.',
    keywords:
      'auditable AI, AI compliance, air-gapped deployment, data residency, immutable audit trail, content-addressed storage, on-premise AI security, no GPU AI, AI governance',
    url: `${baseUrl}/trust`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'Trust & Security', url: '/trust' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${baseUrl}/trust#webpage`,
        url: `${baseUrl}/trust`,
        name: 'Trust, Security & Compliance — Codentic AI',
        about: { '@id': orgId },
        isPartOf: { '@id': siteId }
      }
    ]
  },

  partners: {
    title: 'Design Partner Program — Run the Loop on Your Own Model | Codentic AI',
    description:
      'Run the EWM side-car on your own host model, in your own infrastructure. We measure round-trip fidelity, structural recall and novelty, and before/after context cost — on the hardware you actually run.',
    keywords:
      'design partner program, AI pilot, side-car integration, adapter SDK, host model integration, AI memory pilot, measured context cost, on-premise AI pilot',
    url: `${baseUrl}/partners`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'Design Partners', url: '/partners' }
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${baseUrl}/partners#webpage`,
        url: `${baseUrl}/partners`,
        name: 'Design Partner Program — Codentic AI',
        about: { '@id': orgId },
        isPartOf: { '@id': siteId }
      }
    ]
  },

  privacy: {
    title: 'Privacy Policy | Codentic AI',
    description:
      'How Codentic AI handles information submitted through this website — contact enquiries and NanoLM document analysis.',
    keywords: 'privacy policy, data protection, Codentic AI privacy',
    url: `${baseUrl}/privacy`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'Privacy Policy', url: '/privacy' }
      ])
    ]
  },

  terms: {
    title: 'Terms of Use | Codentic AI',
    description:
      'The terms that apply to your use of the Codentic AI website and its content, including research-demonstration and clinical disclaimers.',
    keywords: 'terms of use, terms and conditions, Codentic AI terms',
    url: `${baseUrl}/terms`,
    structuredData: [
      breadcrumb([
        { name: 'Home', url: '/' },
        { name: 'Terms of Use', url: '/terms' }
      ])
    ]
  }
};

/* ------------------------------------------------------------------ */
/*  Public helpers                                                    */
/* ------------------------------------------------------------------ */

/** Get SEO data for a known page key (falls back to home). */
export const getSEOData = (page) => seoData[page] || seoData.home;

/* ------------------------------------------------------------------ */
/*  Internal helpers                                                  */
/* ------------------------------------------------------------------ */

function productItem(position, name, description) {
  return {
    '@type': 'ListItem',
    position,
    item: {
      '@type': 'Product',
      name,
      description,
      brand: { '@id': orgId }
    }
  };
}
